/**
 * Missing Prices review — one simple screen that appears ONLY when the
 * operator tries to complete an inventory count but some items have
 * no valid price. UX principle: minimal, human-readable, no jargon.
 *
 * For every unresolved row we show:
 *   · product name
 *   · counted quantity + unit
 *   · short human reason (no internal enum names)
 *   · one-tap suggestions (only when unit-safe)
 *   · manual unit-price input
 *   · optional checkbox "Also update Product Master" (unchecked by default)
 */
import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { AlertTriangle, Check, Info } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { apiErrorMessage } from "@/lib/apiErrorMessage";

// Known backend reason texts → translation key (display only).
const REASON_KEY = {
    "Missing barcode or product link": "missingPrices.reason.noLink",
    "Unknown product": "missingPrices.reason.unknownProduct",
    "No confirmed delivery found · product has no saved price": "missingPrices.reason.noDeliveryNoSaved",
    "Product has no unit defined": "missingPrices.reason.noUnit",
    "No price found": "missingPrices.reason.noPrice",
};

const fmt = (n) => Number(n || 0).toFixed(4).replace(/\.?0+$/, "");

const sourceLabels = {
    confirmed_delivery: "Delivery Prices",
    product_master: "Saved Product Prices",
    manual_item: "Manual Prices",
    manual_inventory: "Manual Prices",
    legacy: "Older items",
};

export function MissingPricesReview({ countId, currency = "EUR",
                                        open, onClose, onResolved }) {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [payload, setPayload] = useState(null);
    const [drafts, setDrafts] = useState({});
    const [updateMaster, setUpdateMaster] = useState({});
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/inventory-counts/${countId}/missing-prices`);
            setPayload(data);
            // Seed drafts with best-available suggestion (unit-safe only).
            const d = {};
            for (const it of data.items || []) {
                const del = it.suggestions?.confirmed_delivery?.converted_price;
                const mst = it.suggestions?.product_master?.converted_price;
                if (typeof del === "number") d[it.id] = del;
                else if (typeof mst === "number") d[it.id] = mst;
            }
            setDrafts(d);
        } catch (err) {
            toast.error(apiErrorMessage(err)
                || t("missingPrices.couldNotLoad", "Could not load Missing Prices"));
        } finally { setLoading(false); }
    }, [countId]);

    useEffect(() => { if (open) load(); }, [open, load]);

    if (!open) return null;

    const applyAndClose = async () => {
        if (!payload) return;
        const updates = (payload.items || [])
            .map((it) => ({
                item_id: it.id,
                price: Number(drafts[it.id] ?? 0),
                apply_to_product_master: !!updateMaster[it.id],
            }))
            .filter((u) => u.price > 0);
        if (updates.length === 0) {
            toast.error(t("missingPrices.enterOne", "Enter a unit price for at least one item"));
            return;
        }
        setSaving(true);
        try {
            await api.post(`/inventory-counts/${countId}/missing-prices/apply`, { updates });
            toast.success(t("missingPrices.saved", { defaultValue: "Saved {{count}} prices", count: updates.length }));
            await load();
            if (onResolved) onResolved();
        } catch (err) {
            toast.error(apiErrorMessage(err)
                || t("missingPrices.couldNotSave", "Could not save prices"));
        } finally { setSaving(false); }
    };

    const canComplete = !!(payload && payload.missing_count === 0);
    const items = payload?.items || [];

    return (
        <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
            <DialogContent className="max-w-3xl" data-testid="missing-prices-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <AlertTriangle className="h-5 w-5 text-amber-600" />
                        {t("missingPrices.title", "Missing prices")}
                    </DialogTitle>
                    <DialogDescription>
                        {t("missingPrices.desc", "Enter a unit price for each product below to complete this inventory.")}
                    </DialogDescription>
                </DialogHeader>

                {loading && (
                    <p className="text-sm text-neutral-500 py-6 text-center">{t("common.loading", "Loading…")}</p>
                )}

                {!loading && canComplete && (
                    <div className="py-8 text-center" data-testid="missing-prices-cleared">
                        <Check className="h-10 w-10 text-emerald-500 mx-auto mb-3" />
                        <p className="text-sm text-neutral-700">{t("missingPrices.allResolved", "All prices resolved. You can complete the inventory.")}</p>
                    </div>
                )}

                {!loading && !canComplete && items.length > 0 && (
                    <div className="max-h-[60vh] overflow-y-auto -mx-2 px-2">
                        <ul className="divide-y divide-neutral-200">
                            {items.map((it) => {
                                const delivery = it.suggestions?.confirmed_delivery;
                                const master = it.suggestions?.product_master;
                                const historical = it.suggestions?.historical_reference;
                                const canDelivery = delivery && typeof delivery.converted_price === "number";
                                const canMaster = master && typeof master.converted_price === "number";
                                return (
                                    <li key={it.id} className="py-4"
                                         data-testid={`missing-row-${it.id}`}>
                                        <div className="flex items-start justify-between gap-4">
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm font-semibold text-neutral-900 truncate">
                                                    {it.product_name}
                                                </p>
                                                <p className="text-xs text-neutral-500 mt-0.5">
                                                    {Number(it.quantity).toString()} {it.unit || ""}
                                                </p>
                                                <p className="text-xs text-amber-700 mt-1 flex items-center gap-1"
                                                    data-testid={`missing-reason-${it.id}`}>
                                                    <Info className="h-3 w-3" /> {REASON_KEY[it.reason] ? t(REASON_KEY[it.reason], it.reason) : (/^Unit mismatch/.test(it.reason || "") ? it.reason.replace(/^Unit mismatch \(product: (.*), inventory: (.*)\)$/, (_m, a, b) => t("missingPrices.reason.unitMismatch", { defaultValue: "Unit mismatch (product: {{product}}, inventory: {{inventory}})", product: a, inventory: b })) : it.reason)}
                                                </p>
                                                {(canDelivery || canMaster) && (
                                                    <div className="mt-2 flex flex-wrap gap-1.5">
                                                        {canDelivery && (
                                                            <button type="button"
                                                                onClick={() => setDrafts((d) => ({ ...d, [it.id]: delivery.converted_price }))}
                                                                data-testid={`apply-delivery-${it.id}`}
                                                                className="text-[11px] px-2 py-1 rounded border border-emerald-200 bg-emerald-50 text-emerald-800 hover:bg-emerald-100">
                                                                {t("missingPrices.useDelivery", "Use delivery price")} {currency} {fmt(delivery.converted_price)}/{it.unit}
                                                                {delivery.delivery_date ? ` · ${delivery.delivery_date}` : ""}
                                                            </button>
                                                        )}
                                                        {canMaster && (
                                                            <button type="button"
                                                                onClick={() => setDrafts((d) => ({ ...d, [it.id]: master.converted_price }))}
                                                                data-testid={`apply-master-${it.id}`}
                                                                className="text-[11px] px-2 py-1 rounded border border-neutral-200 bg-neutral-50 text-neutral-700 hover:bg-neutral-100">
                                                                {t("missingPrices.useSaved", "Use saved price")} {currency} {fmt(master.converted_price)}/{it.unit}
                                                            </button>
                                                        )}
                                                    </div>
                                                )}
                                                {historical && (
                                                    <p className="text-[11px] text-neutral-400 mt-1"
                                                        data-testid={`historical-ref-${it.id}`}>
                                                        {t("missingPrices.forReference", { defaultValue: "For reference — last count on {{date}}:", date: historical.previous_count_date })} {currency} {fmt(historical.previous_price)}/{historical.previous_unit}
                                                    </p>
                                                )}
                                            </div>
                                            <div className="w-40 shrink-0">
                                                <label className="block text-[11px] font-semibold text-neutral-500 mb-1">
                                                    {t("missingPrices.unitPrice", { defaultValue: "Unit price ({{currency}})", currency })}
                                                </label>
                                                <input type="number" min="0" step="0.0001"
                                                    value={drafts[it.id] ?? ""}
                                                    onChange={(e) => setDrafts((d) => ({ ...d, [it.id]: e.target.value }))}
                                                    data-testid={`price-input-${it.id}`}
                                                    className="w-full h-9 px-2 rounded border border-neutral-300 text-sm focus:border-neutral-900 focus:ring-0" />
                                                {it.product_id && (
                                                    <label className="mt-2 flex items-center gap-1.5 text-[11px] text-neutral-500 cursor-pointer">
                                                        <input type="checkbox"
                                                            checked={!!updateMaster[it.id]}
                                                            onChange={(e) => setUpdateMaster((u) => ({ ...u, [it.id]: e.target.checked }))}
                                                            data-testid={`update-master-${it.id}`}
                                                            className="h-3.5 w-3.5" />
                                                        {t("missingPrices.alsoUpdateSaved", "Also update saved price")}
                                                    </label>
                                                )}
                                            </div>
                                        </div>
                                    </li>
                                );
                            })}
                        </ul>
                    </div>
                )}

                <DialogFooter>
                    <button onClick={onClose} disabled={saving}
                        data-testid="missing-prices-cancel"
                        className="h-10 px-3 rounded-md border border-neutral-300 text-sm font-semibold hover:bg-neutral-50">
                        {t("common.cancel", "Cancel")}
                    </button>
                    {!canComplete && (
                        <button onClick={applyAndClose} disabled={saving || loading}
                            data-testid="missing-prices-apply"
                            className="h-10 px-4 rounded-md bg-neutral-900 text-white text-sm font-semibold hover:bg-neutral-800 disabled:opacity-60">
                            {t("missingPrices.savePrices", "Save prices")}
                        </button>
                    )}
                    {canComplete && (
                        <button onClick={onClose}
                            data-testid="missing-prices-continue"
                            className="h-10 px-4 rounded-md bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700">
                            {t("missingPrices.continue", "Continue")}
                        </button>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

/**
 * Compact post-completion summary. Shows the operator immediately how
 * valuation quality broke down (4 human lines, no jargon).
 */
export function InventoryCompletionSummary({ open, onClose, summary,
                                                currency = "EUR" }) {
    const { t } = useTranslation();
    if (!open || !summary) return null;
    const total = summary.total_items || 0;
    const by = summary.by_source || {};
    const del = by.confirmed_delivery || { count: 0, value: 0 };
    const master = by.product_master || { count: 0, value: 0 };
    const manual = {
        count: (by.manual_item?.count || 0) + (by.manual_inventory?.count || 0),
        value: (by.manual_item?.value || 0) + (by.manual_inventory?.value || 0),
    };
    const legacy = by.legacy || { count: 0, value: 0 };
    const row = (label, entry, testid) => (
        <div className="flex items-center justify-between py-1.5" data-testid={testid}>
            <span className="text-sm text-neutral-600">{label}</span>
            <span className="text-sm font-semibold text-neutral-900 tabular-nums">
                {entry.count} / {total} · {currency} {Number(entry.value || 0).toFixed(2)}
            </span>
        </div>
    );
    return (
        <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
            <DialogContent className="max-w-md" data-testid="inventory-summary-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Check className="h-5 w-5 text-emerald-500" /> {t("missingPrices.inventoryCompleted", "Inventory completed")}
                    </DialogTitle>
                </DialogHeader>
                <div className="mt-2">
                    <div className="flex items-baseline justify-between border-b border-neutral-200 pb-3 mb-3">
                        <span className="text-sm text-neutral-500">{t("missingPrices.totalValue", "Total value")}</span>
                        <span className="text-2xl font-extrabold tracking-tight" data-testid="summary-total-value">
                            {currency} {Number(summary.total_value || 0).toFixed(2)}
                        </span>
                    </div>
                    {row(t("missingPrices.usingDelivery", "Products using Delivery Prices"), del, "summary-line-delivery")}
                    {row(t("missingPrices.usingSaved", "Products using Saved Product Prices"), master, "summary-line-master")}
                    {row(t("missingPrices.usingManual", "Products using Manual Prices"), manual, "summary-line-manual")}
                    {legacy.count > 0 && row(t("missingPrices.olderItems", "Older items"), legacy, "summary-line-legacy")}
                </div>
                <DialogFooter>
                    <button onClick={onClose}
                        data-testid="summary-close"
                        className="h-10 px-4 rounded-md bg-neutral-900 text-white text-sm font-semibold hover:bg-neutral-800">
                        {t("missingPrices.done", "Done")}
                    </button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
